import { useCallback, useEffect, useState } from "react";

import isEmpty from "lodash/isEmpty";

import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import type { FlashListRef } from "@/ui/layout/list/FlatList";

interface UseCarouselUserChangeProps<T = unknown> {
  analyticsComponent: string;
  analyticsSource: string;
  scrollableRef?: React.RefObject<FlashListRef<T> | null>;
}

interface UseCarouselUserChange {
  handleUserChange: (userId: string) => void;
  selectedUserId: string;
}

/**
 * Shared hook for carousel components with multi-user support.
 * Manages user selection state, deduplication, analytics, and optional scroll reset.
 *
 * @param analyticsComponent - Component name for analytics tracking
 * @param analyticsSource - Source identifier for analytics tracking
 * @param scrollableRef - Optional ref to a FlashList for scroll reset on user change
 * @returns Object with selectedUserId state and handleUserChange callback
 *
 * @example
 * const { selectedUserId, handleUserChange } = useCarouselUserChange({
 *   analyticsComponent: AnalyticsComponent.Tab,
 *   analyticsSource: MyAnalyticsSources.Section,
 *   scrollableRef: flatListRef,
 * });
 */
const useCarouselUserChange = <T = unknown>({
  analyticsComponent,
  analyticsSource,
  scrollableRef,
}: UseCarouselUserChangeProps<T>): UseCarouselUserChange => {
  const { trackUserSelectorEvent } = useAnalytics();
  const { externalId: appUserId } = useAppUserSelector();

  const [selectedUserId, setSelectedUserId] = useState<string>(appUserId);

  // Initialize selectedUserId from appUserId when appUserId becomes available
  // (appUserId may be empty string on initial render during login flow)
  useEffect(() => {
    if (!isEmpty(selectedUserId)) return;
    setSelectedUserId(appUserId);
  }, [selectedUserId, appUserId]);

  const handleUserChange = useCallback(
    (userId: string) => {
      // Deduplicate: don't process if already selected
      if (userId === selectedUserId) return;

      // Track analytics event
      trackUserSelectorEvent({
        component: analyticsComponent,
        selectedUser: userId,
        source: analyticsSource,
      });

      // Scroll to top if ref provided
      // eslint-disable-next-line @typescript-eslint/naming-convention -- React Native API uses `animated` parameter
      scrollableRef?.current?.scrollToOffset({ animated: false, offset: 0 });

      // Update selected user
      setSelectedUserId(userId);
    },
    [
      analyticsComponent,
      analyticsSource,
      scrollableRef,
      selectedUserId,
      trackUserSelectorEvent,
    ],
  );

  return {
    handleUserChange,
    selectedUserId,
  };
};

export default useCarouselUserChange;
